import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  ArrowUpRight, 
  Plus, 
  Trash2, 
  Loader2, 
  AlertCircle, 
  Calendar, 
  User, 
  FileText 
} from 'lucide-react';
import { useStore } from '../../hooks/useStore.js';
import { groupInventoryByProduct, sortLotsByExpiry } from '../../utils/sorting.js';
import { takeBoxContents } from '../../utils/box-contents.js';
import { addTransaction, updateInventoryItem } from '../../state/store.js';
import { saveLocalData } from '../../state/storage.js';
import { syncAfterLocalChange } from '../../services/sync.js';
import { getAuthenticatedOperatorName, getAuthenticatedOperatorEmail } from '../../services/auth.js';
import { hasCloudSyncConfig } from '../../services/supabase.js';
import { rpcBorrowInventory } from '../../services/rpc.js';

export function BorrowModal({ 
  isOpen, 
  preselectedProductKeys = [], 
  onClose, 
  onSuccess 
}) {
  const inventory = useStore(s => s.inventory) || [];
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [borrowerName, setBorrowerName] = useState('');
  const [borrowDate, setBorrowDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [basket, setBasket] = useState([]); // [{ productKey, quantity }]

  const activeProducts = useMemo(() => {
    const activeItems = inventory.filter(i => !i.deleted);
    return Array.from(groupInventoryByProduct(activeItems).values())
      .filter(g => g.totalQuantity > 0);
  }, [inventory]);

  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toISOString().split('T')[0];
    setBorrowDate(today);

    // Default due date: 7 days from now
    const d = new Date();
    d.setDate(d.getDate() + 7);
    setDueDate(d.toISOString().split('T')[0]);

    const defaultOperator = getAuthenticatedOperatorName() || getAuthenticatedOperatorEmail() || 'เจ้าหน้าที่คลัง SDR';
    setBorrowerName(defaultOperator);
    setPurpose('');
    setErrorMsg('');

    if (preselectedProductKeys.length > 0) {
      setBasket(preselectedProductKeys.map(k => ({ productKey: k, quantity: 1 })));
    } else if (activeProducts.length > 0) {
      setBasket([{ productKey: activeProducts[0].key, quantity: 1 }]);
    } else {
      setBasket([]);
    }
  }, [isOpen, preselectedProductKeys, activeProducts]);

  if (!isOpen) return null;

  const handleAddRow = () => {
    if (activeProducts.length === 0) return;
    setBasket(prev => [...prev, { productKey: activeProducts[0].key, quantity: 1 }]);
  };

  const handleRemoveRow = (index) => {
    setBasket(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateRow = (index, field, value) => {
    setBasket(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (basket.length === 0) {
      setErrorMsg('กรุณาเลือกรายการสินค้าที่ต้องการยืม');
      return;
    }

    if (!borrowerName.trim()) {
      setErrorMsg('กรุณาระบุชื่อผู้ยืม');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const nowIso = new Date().toISOString();
      const operatorEmail = getAuthenticatedOperatorEmail() || 'user';
      const pickingAllocations = [];

      for (let i = 0; i < basket.length; i++) {
        const itemReq = basket[i];
        const reqQty = Number(itemReq.quantity) || 0;
        if (reqQty <= 0) continue;

        const product = activeProducts.find(p => p.key === itemReq.productKey);
        if (!product) continue;

        if (reqQty > product.totalQuantity) {
          throw new Error(`ยอดขอยืมของ "${product.name}" (${reqQty}) เกินจำนวนคงเหลือที่มี (${product.totalQuantity})`);
        }

        // Online RPC attempt
        if (hasCloudSyncConfig()) {
          try {
            await rpcBorrowInventory(product.key, reqQty, borrowerName.trim(), purpose.trim(), dueDate);
          } catch (rpcErr) {
            console.warn('RPC borrow fallback to local transaction:', rpcErr);
          }
        }

        // Local FEFO deduction across lots
        let remainingToDeduct = reqQty;
        const sortedLots = sortLotsByExpiry(product.lots || []);

        for (let l = 0; l < sortedLots.length; l++) {
          if (remainingToDeduct <= 0) break;
          const lot = sortedLots[l];
          const availableInLot = Number(lot.quantity) || 0;
          if (availableInLot <= 0) continue;

          const deductFromThisLot = Math.min(availableInLot, remainingToDeduct);
          const newLotQty = Number((availableInLot - deductFromThisLot).toFixed(5));

          let newBoxContents = lot.boxContents;
          if (Array.isArray(lot.boxContents) && lot.boxContents.length > 0) {
            newBoxContents = takeBoxContents(lot.boxContents, deductFromThisLot);
          }

          // Record picking guide info
          pickingAllocations.push({
            itemName: product.name,
            brand: product.brand,
            size: product.size,
            unit: product.unit,
            lot: lot.lot || 'ล็อตหลัก',
            location: lot.location,
            quantity: deductFromThisLot
          });

          // Record individual transaction
          const txId = `tx_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
          addTransaction({
            id: txId,
            itemId: lot.id,
            itemName: product.name,
            type: 'borrow',
            quantity: deductFromThisLot,
            unit: product.unit || 'ชิ้น',
            borrowerName: borrowerName.trim(),
            notes: purpose.trim() ? `${purpose.trim()} (ครบกำหนด: ${dueDate || 'ไม่ระบุ'})` : `ครบกำหนด: ${dueDate || 'ไม่ระบุ'}`,
            dueDate: dueDate || '',
            timestamp: nowIso,
            operatorEmail: operatorEmail,
            deleted: false
          });

          // Update lot balance in store
          updateInventoryItem({
            ...lot,
            quantity: newLotQty,
            boxContents: newBoxContents,
            updatedAt: nowIso,
            updatedBy: operatorEmail
          });

          remainingToDeduct -= deductFromThisLot;
        }
      }

      saveLocalData();
      syncAfterLocalChange('borrow-transaction');

      onClose();
      if (onSuccess) onSuccess(pickingAllocations);
    } catch (err) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการบันทึกการยืม');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150 my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-amber-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-800">
              <ArrowUpRight className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-slate-900">บันทึกการยืมพัสดุ</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Borrower & Due Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                ชื่อผู้ยืม <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  required
                  value={borrowerName}
                  onChange={(e) => setBorrowerName(e.target.value)}
                  placeholder="ระบุชื่อ-นามสกุล หรือรหัสนักศึกษา"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-800 mb-1">
                กำหนดส่งคืน (Due Date)
              </label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-800 mb-1">
              วัตถุประสงค์ / กิจกรรมที่นำไปใช้
            </label>
            <div className="relative">
              <FileText className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <textarea
                rows={2}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="เช่น ใช้งานโครงการ Open House, ใช้ซ้อมกิจกรรมแล็บ"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-300 bg-white text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Borrow Items Basket */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                รายการพัสดุที่ขอยืม ({basket.length} รายการ)
              </span>
              <button
                type="button"
                onClick={handleAddRow}
                className="text-xs text-amber-700 hover:text-amber-800 font-semibold flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>เพิ่มพัสดุ</span>
              </button>
            </div>

            <div className="space-y-2">
              {basket.map((row, idx) => {
                const selectedProd = activeProducts.find(p => p.key === row.productKey);
                return (
                  <div key={idx} className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200 bg-slate-50/70">
                    <select
                      value={row.productKey}
                      onChange={(e) => handleUpdateRow(idx, 'productKey', e.target.value)}
                      className="flex-1 px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 bg-white font-medium"
                    >
                      {activeProducts.map(p => (
                        <option key={p.key} value={p.key}>
                          {p.name} {p.brand ? `(${p.brand})` : ''} - คงเหลือ {p.totalQuantity} {p.unit}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      min="1"
                      max={selectedProd ? selectedProd.totalQuantity : 9999}
                      value={row.quantity}
                      onChange={(e) => handleUpdateRow(idx, 'quantity', e.target.value)}
                      className="w-20 px-2 py-1.5 text-xs rounded-lg border border-slate-200 bg-white font-bold text-center"
                    />

                    <span className="text-xs text-slate-500 w-12 truncate">
                      {selectedProd?.unit || 'ชิ้น'}
                    </span>

                    {basket.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="text-slate-400 hover:text-red-500 p-1 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-xs shadow-xs transition-colors cursor-pointer"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-900" />}
              <span>{saving ? 'กำลังประมวลผล...' : 'ยืนยันการยืมพัสดุ'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
